import { GetServerSideProps } from "next";
import Head from "next/head";
import MineralDetails from "@/component/mineralDetails";
import { getMineral } from "@/utils/apis/minerals/explore-detail.api";
import { getMineralSeo } from "@/utils/apis/minerals/mineral-seo.api";
import { IGetMineral } from "@/interfaces/add-mineral-specimen.interface";
import APIConstants from "@/constants/api.constants";
import { DefaultCanonical, PageKeywords, metaDescription, metaTitle } from "@/component/seo/DefaultSeo";

interface MineralDetailPageProps {
  mineralDatas: IGetMineral;
  seoData: any;
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { slug } = context.query as { slug: string };
  const id = decodeURIComponent(slug);

  try {
    const response = await getMineral(id);
    const mineralDatas = response.data.data;

    let seoData = null;
    try {
      const mineralId = mineralDatas?._id || id;
      const seoResponse = await getMineralSeo(mineralId);
      if (seoResponse.data.success) {
        const rawSeoData = seoResponse.data.data;
        seoData = Array.isArray(rawSeoData) ? rawSeoData[0] : rawSeoData;
      }
    } catch (seoError) {
      console.error("Error fetching mineral SEO:", seoError);
    }

    return {
      props: {
        mineralDatas,
        seoData,
      },
    };
  } catch (error) {
    console.error("Error fetching mineral data:", error);

    return {
      props: {
        mineralDatas: null,
        seoData: null,
      },
    };
  }
};

function MineralDetailPage({ mineralDatas, seoData }: MineralDetailPageProps) {
  if (!mineralDatas) {
    return <div>Mineral not found.</div>;
  }


  const originalTitle =
    seoData?.title || mineralDatas.specimentTitle || "Mineral Details";
  const originalDescription = seoData?.description || mineralDatas.notes || "";
  const rawKeywords =
    seoData?.keywords ||
    seoData?.metaTags ||
    seoData?.metaTag ||
    "mineral, details, specimen";
  const originalKeywords = Array.isArray(rawKeywords)
    ? rawKeywords.join(", ")
    : rawKeywords;

  const seoTitle = [
    originalTitle,
    originalDescription || "",
    originalKeywords || "",
  ]
    .filter(Boolean)
    .join(" | ");

  const seoImage =
    mineralDatas.images?.length > 0
      ? `${APIConstants.image_url}${mineralDatas.images[0].path}`
      : "";

  return (
    <>
      <DefaultCanonical url={seoData?.canonicalUrl} />
      <PageKeywords keywords={originalKeywords === "mineral, details, specimen" ? undefined : originalKeywords} title={originalTitle} description={originalDescription} />
      <Head>
        <title key="title">{metaTitle(originalTitle, seoData?.title)}</title>
        <meta
          key="description"
          name="description"
          content={metaDescription(seoData?.description || mineralDatas.notes || "", seoData?.description)}
        />
        <meta
          key="og:title"
          property="og:title"
          content={
            seoData?.title || mineralDatas.specimentTitle || "Mineral Details"
          }
        />
        <meta
          key="og:description"
          property="og:description"
          content={metaDescription(seoData?.description || mineralDatas.notes || "", seoData?.description)}
        />
        <meta key="og:image" property="og:image" content={seoImage} />
        <meta key="og:type" property="og:type" content="website" />
        <meta
          key="twitter:card"
          name="twitter:card"
          content="summary_large_image"
        />
        <meta
          key="twitter:title"
          name="twitter:title"
          content={
            seoData?.title || mineralDatas.specimentTitle || "Mineral Details"
          }
        />
        <meta
          key="twitter:description"
          name="twitter:description"
          content={seoData?.description || mineralDatas.notes || ""}
        />
        <meta key="twitter:image" name="twitter:image" content={seoImage} />
        <meta
          key="viewport"
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
        />
      </Head>

      <MineralDetails
        type="explore"
        mineralDatas={mineralDatas}
        mineralSeo={seoData}
      />
    </>
  );
}

export default MineralDetailPage;
